import React, { Suspense, lazy, useEffect, useState } from "react";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { ReactNotifications } from "react-notifications-component";
import { Dna } from "react-loader-spinner";
import "react-pro-sidebar/dist/css/styles.css";
import "devextreme/dist/css/dx.light.css";
import "./App.css";
import ChatRoomListPage from "./pages/ChatRoomListPage";
import ChatRoomPage from "./pages/ChatRoomPage";
import PublicChatRoomListPage from "./pages/PublicChatRoomListPage";

const TicketListPage = lazy(() => import("./pages/TicketListPage"));
const TicketPage = lazy(() => import("./pages/TicketPage"));
const LoginViaMobilePage = lazy(() => import("./pages/LoginViaMobilePage"));
const LoginLayout = lazy(() => import("./layouts/LoginLayout"));
const SaleContract = lazy(() => import("./containers/SaleContract"));
const RootLayout = lazy(() => import("./layouts/RootLayout"));
const LoginPage = lazy(() => import("./pages/LoginPage"));
const RegisterPage = lazy(() => import("./pages/RegisterPage"));
const DashboardPage = lazy(() => import("./pages/DashboardPage"));
const ContactPage = lazy(() => import("./pages/ContactPage"));
const ContactListPage = lazy(() => import("./pages/ContactListPage"));
const ReceiveListPage = lazy(() => import("./pages/ReceiveListPage"));
const PayListPage = lazy(() => import("./pages/PayListPage"));
const ServicePage = lazy(() => import("./pages/ServicePage"));
const ServiceListPage = lazy(() => import("./pages/ServiceListPage"));
const ReceivePage = lazy(() => import("./pages/ReceivePage"));
const PayPage = lazy(() => import("./pages/PayPage"));
const SalesPage = lazy(() => import("./pages/SalesPage"));
const SaleViewPage = lazy(() => import("./pages/SaleViewPage"));
const SalesListPage = lazy(() => import("./pages/SalesListPage"));
const BankPage = lazy(() => import("./pages/BankPage"));
const BankListPage = lazy(() => import("./pages/BankListPage"));
const CashListPage = lazy(() => import("./pages/CashListPage"));
const TransferPage = lazy(() => import("./pages/TransferPage"));
const TransferListPage = lazy(() => import("./pages/TransferListPage"));
const ReceivedChecksListPage = lazy(() =>
  import("./pages/ReceivedChecksListPage")
);
const PayedChecksListPage = lazy(() => import("./pages/PayedChecksListPage"));
const BillPage = lazy(() => import("./pages/BillPage"));
const BillsListPage = lazy(() => import("./pages/BillsListPage"));
const BillsViewPage = lazy(() => import("./pages/BillViewPage"));
const SaleViewPageDev = lazy(() => import("./pages/SaleViewPageDev"));
const AccountingDocumentPage = lazy(() =>
  import("./pages/AccountingDocumentPage")
);
const CashTransactionsPage = lazy(() => import("./pages/CashTransactionsPage"));
const BankTransactionsPage = lazy(() => import("./pages/BankTransactionsPage"));
const UserPage = lazy(() => import("./pages/UserPage"));
const UserListPage = lazy(() => import("./pages/UserListPage"));
const SettingsPage = lazy(() => import("./pages/SettingsPage"));
const SingleNotification = lazy(() => import("./pages/SingleNotification"));
const NotificationPage = lazy(() => import("./pages/NotificationPage"));
const ReceiveCheckPage = lazy(() => import("./pages/ReceiveCheckPage"));
const AccessPage = lazy(() => import("./pages/AccessPage"));
const AccessListPage = lazy(() => import("./pages/AccessListPage"));
const LogListPage = lazy(() => import("./pages/LogListPage"));
const JobListPage = lazy(() => import("./pages/JobListPage"));
const SaleSharePage = lazy(() => import("./pages/SaleSharePage"));
const UserTypeListPage = lazy(() => import("./pages/UserTypeListPage"));
const SaleInvoiceFormal = lazy(() => import("./containers/SaleInvoiceFormal"));

function loadLanguageSpecificCSS() {
  const rtlCSSPath = "/css/semantic.rtl.min.css";
  const linkElement = document.getElementById("language-stylesheet");
  linkElement.href = rtlCSSPath;
}

const App = () => {
  useEffect(() => {
    let ignore = false;
    if (!ignore) {
      loadLanguageSpecificCSS();
    }
    return () => {
      ignore = true;
    };
  }, []);

  return (
    <BrowserRouter>
      <ReactNotifications />
      <Suspense
        fallback={
          <Dna
            visible={true}
            height="80"
            width="80"
            ariaLabel="dna-loading"
            wrapperStyle={{
              justifyContent: "center",
              width: "100%",
              alignItems: "center",
            }}
            wrapperClass="dna-wrapper"
          />
        }
      >
        <Routes>
          <Route element={<LoginLayout />}>
            <Route exact path="/register" element={<RegisterPage />} />
          </Route>
          <Route element={<LoginLayout />}>
            <Route exact path="/login" element={<LoginPage />} />
          </Route>
          <Route element={<LoginLayout />}>
            <Route exact path="/signin" element={<LoginViaMobilePage />} />
          </Route>
          <Route element={<RootLayout />}>
            <Route exact path="/" element={<DashboardPage />} />
          </Route>
          <Route element={<RootLayout />}>
            <Route exact path="/log-list" element={<LogListPage />} />
          </Route>
          <Route element={<RootLayout />}>
            <Route exact path="/access" element={<AccessPage />} />
          </Route>
          <Route element={<RootLayout />}>
            <Route exact path="/access-edit/:id" element={<AccessPage />} />
          </Route>
          <Route element={<RootLayout />}>
            <Route exact path="/access-list" element={<AccessListPage />} />
          </Route>
          <Route element={<RootLayout />}>
            <Route exact path="/usertype-list" element={<UserTypeListPage />} />
          </Route>
          <Route element={<RootLayout />}>
            <Route exact path="/contact" element={<ContactPage />} />
          </Route>
          <Route element={<RootLayout />}>
            <Route exact path="/contact-edit/:id" element={<ContactPage />} />
          </Route>
          <Route element={<RootLayout />}>
            <Route exact path="/contact-list" element={<ContactListPage />} />
          </Route>
          <Route element={<RootLayout />}>
            <Route exact path="/receive" element={<ReceivePage />} />
          </Route>
          <Route element={<RootLayout />}>
            <Route exact path="/receive-list" element={<ReceiveListPage />} />
          </Route>
          <Route element={<RootLayout />}>
            <Route exact path="/receive-edit/:id" element={<ReceivePage />} />
          </Route>
          <Route element={<RootLayout />}>
            <Route
              exact
              path="/receive-check-edit/:id"
              element={<ReceiveCheckPage />}
            />
          </Route>
          <Route element={<RootLayout />}>
            <Route exact path="/pay" element={<PayPage />} />
          </Route>
          <Route element={<RootLayout />}>
            <Route exact path="/pay-list" element={<PayListPage />} />
          </Route>
          <Route element={<RootLayout />}>
            <Route exact path="/pay-edit/:id" element={<PayPage />} />
          </Route>
          <Route element={<RootLayout />}>
            <Route exact path="/service" element={<ServicePage />} />
          </Route>
          <Route element={<RootLayout />}>
            <Route exact path="/service-edit/:id" element={<ServicePage />} />
          </Route>
          <Route element={<RootLayout />}>
            <Route exact path="/service-list" element={<ServiceListPage />} />
          </Route>
          <Route element={<RootLayout />}>
            <Route exact path="/sale" element={<SalesPage />} />
          </Route>
          <Route element={<RootLayout />}>
            <Route exact path="/sale-edit/:id" element={<SalesPage />} />
          </Route>
          <Route element={<RootLayout />}>
            <Route exact path="/sales-list" element={<SalesListPage />} />
          </Route>
          <Route element={<RootLayout />}>
            <Route exact path="/sale-share" element={<SaleSharePage />} />
          </Route>
          <Route element={<RootLayout />}>
            <Route exact path="/sale-view/:id" element={<SaleViewPage />} />
          </Route>
          <Route
            exact
            path="/invoice/:saleid"
            element={<SaleInvoiceFormal />}
          />
          <Route
            exact
            path="/contract/:saleidParam"
            element={<SaleContract />}
          />
          <Route exact path="/message/:slug" element={<SingleNotification />} />
          <Route element={<RootLayout />}>
            <Route
              exact
              path="/sale-view-dev/:id"
              element={<SaleViewPageDev />}
            />
          </Route>
          <Route element={<RootLayout />}>
            <Route exact path="/bill" element={<BillPage />} />
          </Route>
          <Route element={<RootLayout />}>
            <Route exact path="/bill-edit/:id" element={<BillPage />} />
          </Route>
          <Route element={<RootLayout />}>
            <Route exact path="/bills-list" element={<BillsListPage />} />
          </Route>
          <Route element={<RootLayout />}>
            <Route exact path="/bill-view/:id" element={<BillsViewPage />} />
          </Route>
          <Route element={<RootLayout />}>
            <Route exact path="/bank" element={<BankPage />} />
          </Route>
          <Route element={<RootLayout />}>
            <Route exact path="/bank-edit/:id" element={<BankPage />} />
          </Route>
          <Route element={<RootLayout />}>
            <Route exact path="/bank-list" element={<BankListPage />} />
          </Route>
          <Route element={<RootLayout />}>
            <Route
              exact
              path="/bank-transactions/:id"
              element={<BankTransactionsPage />}
            />
          </Route>
          <Route element={<RootLayout />}>
            <Route
              exact
              path="/accounting-document/:id"
              element={<AccountingDocumentPage />}
            />
          </Route>
          <Route element={<RootLayout />}>
            <Route exact path="/user" element={<UserPage />} />
          </Route>
          <Route element={<RootLayout />}>
            <Route exact path="/user-edit/:id" element={<UserPage />} />
          </Route>
          <Route element={<RootLayout />}>
            <Route exact path="/user-list" element={<UserListPage />} />
          </Route>
          <Route element={<RootLayout />}>
            <Route exact path="/cash-list" element={<CashListPage />} />
          </Route>
          <Route element={<RootLayout />}>
            <Route
              exact
              path="/cash-transactions/:id"
              element={<CashTransactionsPage />}
            />
          </Route>
          <Route element={<RootLayout />}>
            <Route exact path="/transfer" element={<TransferPage />} />
          </Route>
          <Route element={<RootLayout />}>
            <Route exact path="/transfer-edit/:id" element={<TransferPage />} />
          </Route>
          <Route element={<RootLayout />}>
            <Route exact path="/transfer-list" element={<TransferListPage />} />
          </Route>
          <Route element={<RootLayout />}>
            <Route
              exact
              path="/received-checks-list"
              element={<ReceivedChecksListPage />}
            />
          </Route>
          <Route element={<RootLayout />}>
            <Route
              exact
              path="/payed-checks-list"
              element={<PayedChecksListPage />}
            />
          </Route>
          <Route element={<RootLayout />}>
            <Route exact path="/edit-setting" element={<SettingsPage />} />
          </Route>
          <Route element={<RootLayout />}>
            <Route exact path="/notifications" element={<NotificationPage />} />
          </Route>
          <Route element={<RootLayout />}>
            <Route exact path="/job-list" element={<JobListPage />} />
          </Route>
          <Route element={<RootLayout />}>
            <Route exact path="/ticket" element={<TicketPage />} />
          </Route>
          <Route element={<RootLayout />}>
            <Route
              exact
              path="/ticket-view/:ticketid"
              element={<TicketPage />}
            />
          </Route>
          <Route element={<RootLayout />}>
            <Route exact path="/ticket-list" element={<TicketListPage />} />
          </Route>

          <Route element={<RootLayout />}>
            <Route exact path="/chat-list" element={<ChatRoomListPage />} />
          </Route>
          <Route element={<RootLayout />}>
            <Route exact path="/chat/:roomId" element={<ChatRoomPage />} />
          </Route>

          <Route
            exact
            path="/public/chat/:roomId"
            element={<PublicChatRoomListPage />}
          />
        </Routes>
      </Suspense>
    </BrowserRouter>
  );
};

export default App;
